/**CORE LIBRARIES IMPORT */
import { useEffect, useState } from "react";

/**SERVICES IMPORT */
import { fetchAvailability, getBookingMode } from "../services/booking";

/**
 * Live availability from the server (aggregate numbers only).
 * `includeSimulation` lets the checkout page use the dev simulator; public
 * event pages only ever show real server numbers.
 */
export default function useEventAvailability(event, { includeSimulation = false } = {}) {
  const mode = getBookingMode();
  const enabled = Boolean(event) && (mode === "backend" || (includeSimulation && mode === "simulation"));
  const [state, setState] = useState({ loading: enabled, availability: null, error: false });

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    fetchAvailability(event)
      .then((availability) => !cancelled && setState({ loading: false, availability, error: false }))
      .catch(() => !cancelled && setState({ loading: false, availability: null, error: true }));
    return () => {
      cancelled = true;
    };
  }, [enabled, event]);

  return { ...state, mode, enabled };
}

/** Where "Book Now" should go: online checkout only when sales are really open. */
export const bookingPathFor = (event, availability) =>
  availability?.on_sale && !availability.simulated ? `/events/${event.slug}/book` : `/book?event=${event.slug}`;

/** Public text for the availability row (no invented numbers). */
export function availabilityLabel(availability) {
  if (!availability) return null;
  switch (availability.state) {
    case "on_sale":
      return typeof availability.remaining === "number"
        ? `${availability.remaining} place${availability.remaining === 1 ? "" : "s"} remaining`
        : "Tickets on sale";
    case "sold_out":
      return "Sold out";
    case "paused":
      return "Ticket sales paused";
    case "not_yet_open":
      return "Booking opens soon";
    case "closed":
      return "Online booking closed";
    default:
      return null;
  }
}
